import { Injectable } from '@angular/core';
import { ActionsSubject, ReducerManager, StateObservable, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

interface DebounceOptions {
  debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 10;

@Injectable({
  providedIn: 'root'
})
export class CustomStore<T> extends Store<T> {
  constructor(
    state$: StateObservable,
    actionsObserver: ActionsSubject,
    reducerManager: ReducerManager
  ) {
    super(state$, actionsObserver, reducerManager);
  }

  select<K>(mapFn: (state: T) => K, options?: DebounceOptions): Observable<K>;
  select<K, Props>(mapFn: (state: T, props: Props) => K, props: Props, options?: DebounceOptions): Observable<K>;
  select<a extends keyof T>(key: a, options?: DebounceOptions): Observable<T[a]>;
  select<a extends keyof T, b extends keyof T[a]>(key1: a, key2: b, options?: DebounceOptions): Observable<T[a][b]>;
  select<a extends keyof T, b extends keyof T[a], c extends keyof T[a][b]>(key1: a, key2: b, key3: c, options?: DebounceOptions): Observable<T[a][b][c]>;

  select(...args: any[]): Observable<any> {
    let debounceMs = DEFAULT_DEBOUNCE_MS;

    if (args.length > 0 && typeof args[args.length - 1] === 'object' && 'debounceMs' in args[args.length - 1]) {
      const options = args.pop() as DebounceOptions;
      debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    }

    const originalSelect$ = super.select.apply(this, args as Parameters<Store<T>['select']>);

    if (debounceMs > 0) {
      return originalSelect$.pipe(debounceTime(debounceMs));
    }

    return originalSelect$;
  }
}
